import { useMouseCursor } from '@app/hooks/useMouseCursor.js';
import { pickingModeSelector } from '@app/store/selectors.js';
import { brightnessFilter } from '@features/map/brightness.js';
import { areFeaturesHidden } from '@features/map/featuresOpacity.js';
import {
  FEATURES_PANE_CLASS,
  useOwnPaneOpacity,
} from '@features/map/hooks/featuresPane.js';
import { setMapLeafletElement } from '@features/map/hooks/leafletElementHolder.js';
import { useMap } from '@features/map/hooks/useMap.js';
import { shiftZoomGrid } from '@features/map/shiftZoomGrid.js';
import { snapToDevicePixels } from '@features/map/snapToDevicePixels.js';
import { initialWheelPxPerZoomLevel } from '@features/map/wheelZoomCalibration.js';
import { zoomGridOffset } from '@features/map/zoomGrid.js';
import { HALO_OPACITY, HALO_PANE, HALO_PANE_Z } from '@shared/halo.js';
import { useAppSelector } from '@shared/hooks/useAppSelector.js';
import { useDevicePixelRatio } from '@shared/hooks/useDevicePixelRatio.js';
// Imported for its side effect, before any layer can bind a tooltip.
import '../leafletTooltipFocusFix.js';
// Imported for its side effect, before any tile layer can redraw.
import '../leafletRedrawZoomFix.js';
// Imported for its side effect: registers the `doubleTapDragZoom` map handler.
import '../doubleTapDragZoom.js';
import 'leaflet/dist/leaflet.css';
import { type ReactElement, type ReactNode, useEffect } from 'react';
import { MapContainer, Pane, ScaleControl } from 'react-leaflet';
import './leaflet.css';

type Props = {
  children: ReactNode;
};

export function TheMap({ children }: Props): ReactElement {
  const lat = useAppSelector((state) => state.map.lat);

  const lon = useAppSelector((state) => state.map.lon);

  const zoom = useAppSelector((state) => state.map.zoom);

  const map = useMap();

  useMouseCursor(map?.getContainer());

  const maxZoom = useAppSelector((state) => state.map.maxZoom);

  const zoomSnap = useAppSelector((state) => state.map.zoomSnap);

  // While a dedicated map mode is active (picking a home/photo location, showing
  // a photo location, or drawing an export/cache rectangle), all other map
  // features stay visible but become non-interactive (see leaflet.css) so clicks
  // reach the map and don't hijack the mode.
  const featuresNonInteractive = useAppSelector(pickingModeSelector);

  useEffect(() => {
    map
      ?.getContainer()
      .classList.toggle('fm-features-noninteractive', featuresNonInteractive);
  }, [map, featuresNonInteractive]);

  const backgroundColor = useAppSelector((state) => state.map.backgroundColor);

  const checkerboard = useAppSelector(
    (state) => state.map.backgroundCheckerboard,
  );

  // Whatever no layer covers: no base map, or a transparent one.
  useEffect(() => {
    if (map) {
      const { style } = map.getContainer();

      style.backgroundColor = backgroundColor;

      // The second square is pulled towards grey, so it shows on any colour.
      style.backgroundImage = checkerboard
        ? `repeating-conic-gradient(${backgroundColor} 0 25%, color-mix(in srgb, ${backgroundColor} 80%, #808080) 0 50%)`
        : '';

      style.backgroundSize = checkerboard ? '16px 16px' : '';
    }
  }, [map, backgroundColor, checkerboard]);

  const brightness = useAppSelector((state) => state.map.brightness);

  // `tilePane` holds every map layer, and only them: the features on top keep
  // their own colours.
  useEffect(() => {
    const pane = map?.getPane('tilePane');

    if (pane) {
      pane.style.filter = brightnessFilter(brightness);
    }
  }, [map, brightness]);

  const featuresOpacity = useAppSelector((state) => state.map.featuresOpacity);

  useEffect(() => {
    const container = map?.getContainer();

    container?.style.setProperty(
      '--fm-features-opacity',
      String(featuresOpacity),
    );

    // Too faint to see, too faint to take a click meant for the map.
    container?.classList.toggle(
      'fm-features-faint',
      areFeaturesHidden(featuresOpacity),
    );
  }, [map, featuresOpacity]);

  const haloRef = useOwnPaneOpacity(HALO_OPACITY);

  // Leaflet consults `zoomSnap` on each gesture rather than at construction, so
  // the live map can be retuned in place. Only react-leaflet's own props are
  // fixed at creation, and remounting to change one would refetch every tile and
  // rebuild the vector and radar layers.
  useEffect(() => {
    if (map) {
      map.options.zoomSnap = zoomSnap;
    }
  }, [map, zoomSnap]);

  const zoomOffset = zoomGridOffset(
    useAppSelector((state) => state.map.pixelExact),
    useDevicePixelRatio(),
  );

  useEffect(
    () => (map ? shiftZoomGrid(map, zoomOffset) : undefined),
    [map, zoomOffset],
  );

  useEffect(() => (map ? snapToDevicePixels(map) : undefined), [map]);

  return (
    <MapContainer
      zoomControl={false}
      attributionControl={false}
      maxZoom={maxZoom}
      key={maxZoom}
      ref={setMapLeafletElement}
      center={{ lat, lng: lon }}
      zoom={zoom}
      // The grid the wheel, pinch and box gestures settle on: 1 is a whole zoom
      // level, as Leaflet does by default, and 0 lets them stop anywhere. Only
      // the starting value — the effect above carries later changes.
      zoomSnap={zoomSnap}
      // Only where the wheel starts out: `attachWheelZoomCalibration` measures
      // the device and corrects this from the first gesture on.
      wheelPxPerZoomLevel={initialWheelPxPerZoomLevel(zoomSnap)}
      // One-finger zoom: double-tap, hold the second tap and drag.
      doubleTapDragZoom
    >
      <ScaleControl imperial={false} position="bottomleft" />

      {/* Holds the interactive handles of the active selection mode (export/cache
          rectangle, photo-location marker); exempt from fm-features-noninteractive
          so it stays grabbable while everything else is click-through. */}
      <Pane name="fm-active-overlay" style={{ zIndex: 800 }} />

      {/* Every halo on the map, in one pane so they composite as a single
          group. Declared before the features, which name it. */}
      <Pane
        name={HALO_PANE}
        ref={haloRef}
        className={FEATURES_PANE_CLASS}
        style={{ zIndex: HALO_PANE_Z }}
      />

      {children}
    </MapContainer>
  );
}
